import { useEffect, useState, useCallback } from "react";
import api, { fmtEUR, fmtNum, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

function toISO(d) { return d.toISOString().slice(0, 10); }

function minusYear(iso) {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCFullYear(d.getUTCFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

export default function Ventas() {
  const { branchFilter, org } = useAuth();
  const [data, setData] = useState({ total: 0, receipts: [] });
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [chartFrom, setChartFrom] = useState(toISO(new Date(Date.now() - 29 * 86400000)));
  const [chartTo, setChartTo] = useState(toISO(new Date()));
  const [daily, setDaily] = useState([]);
  const [dailyLY, setDailyLY] = useState([]);
  const [top, setTop] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (branchFilter) params.set("branch_id", branchFilter);
    if (typeFilter !== "all") params.set("receipt_type", typeFilter);
    if (dateFilter) params.set("date", dateFilter);
    api.get(`/loyverse/receipts?${params}`).then((r) => setData(r.data)).catch(() => {});
    const suffix = branchFilter ? `&branch_id=${branchFilter}` : "";
    api.get(`/dashboard/daily?dfrom=${chartFrom}&dto=${chartTo}${suffix}`).then((r) => setDaily(r.data)).catch(() => {});
    api.get(`/dashboard/daily?dfrom=${minusYear(chartFrom)}&dto=${minusYear(chartTo)}${suffix}`)
      .then((r) => setDailyLY(r.data)).catch(() => setDailyLY([]));
    api.get(`/dashboard/top-products?dfrom=${chartFrom}&dto=${chartTo}${suffix}`).then((r) => setTop(r.data)).catch(() => {});
  }, [branchFilter, page, typeFilter, dateFilter, chartFrom, chartTo]);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/loyverse/sync/incremental");
      toast.success(`${r.data.saved} tickets sincronizados`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar");
    } finally { setSyncing(false); }
  };

  const branchNames = org.branches.map((b) => b.name);
  const pages = Math.ceil((data.total || 0) / 25);
  const lyMap = Object.fromEntries(dailyLY.map((r) => [r.date, r.net_sales]));
  const chartData = daily.map((r) => ({ ...r, net_sales_ly: lyMap[minusYear(r.date)] ?? null }));
  const hasLY = chartData.some((r) => r.net_sales_ly !== null);

  return (
    <div className="space-y-8" data-testid="ventas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Ventas</h1>
          <p className="text-sm text-slate-500 mt-1">Tickets Loyverse · devoluciones restan · métodos de pago no duplican ventas</p>
        </div>
        <Button data-testid="ventas-sync-button" onClick={syncNow} disabled={syncing} variant="outline" className="gap-2">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sincronizar
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-400">Periodo gráficos:</span>
        <Input data-testid="ventas-chart-from" type="date" value={chartFrom} onChange={(e) => setChartFrom(e.target.value)} className="w-40 h-9" />
        <span className="text-slate-400 text-sm">→</span>
        <Input data-testid="ventas-chart-to" type="date" value={chartTo} onChange={(e) => setChartTo(e.target.value)} className="w-40 h-9" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="chart-branch-comparison">
          <CardHeader><CardTitle className="text-base font-semibold font-heading">Ventas por sucursal (periodo seleccionado)</CardTitle></CardHeader>
          <CardContent>
            {daily.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-slate-400">Sin datos</div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtEUR(v)} />
                  <Legend />
                  {branchNames.map((n, i) => (
                    <Bar key={n} dataKey={n} stackId="a" fill={i === 0 ? "#D9381E" : "#1E293B"} radius={[3, 3, 0, 0]} />
                  ))}
                  {hasLY && (
                    <Line dataKey="net_sales_ly" name="Año anterior (total)" stroke="#94A3B8"
                      strokeWidth={2} strokeDasharray="5 4" dot={false} />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        <Card data-testid="chart-top-products">
          <CardHeader><CardTitle className="text-base font-semibold font-heading">Top productos (periodo seleccionado)</CardTitle></CardHeader>
          <CardContent>
            {top.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-slate-400">Sin datos</div>
            ) : (
              <div className="space-y-2.5">
                {top.map((p, i) => (
                  <div key={p.product} className="flex items-center justify-between text-sm">
                    <span className="truncate flex-1 text-slate-700">{i + 1}. {p.product}</span>
                    <span className="font-num font-semibold ml-3">{fmtEUR(p.revenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="receipts-table-card">
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
          <CardTitle className="text-base font-semibold font-heading">Tickets ({fmtNum(data.total)})</CardTitle>
          <div className="flex gap-2">
            <Input data-testid="receipts-date-filter" type="date" value={dateFilter}
              onChange={(e) => { setDateFilter(e.target.value); setPage(1); }} className="w-40 h-9" />
            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="receipts-type-filter" className="w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="SALE">Ventas</SelectItem>
                <SelectItem value="REFUND">Devoluciones</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Neto (sin IVA)</TableHead>
                <TableHead className="text-right">IVA</TableHead>
                <TableHead className="text-right">Propina</TableHead>
                <TableHead className="text-right">Total cobrado</TableHead>
                <TableHead className="text-right">Efectivo</TableHead>
                <TableHead className="text-right">Tarjeta</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.receipts.map((r) => (
                <TableRow key={r.receipt_number} data-testid={`receipt-row-${r.receipt_number}`}>
                  <TableCell className="font-num text-xs">#{r.receipt_number}</TableCell>
                  <TableCell className="text-sm">{fmtDate(r.receipt_date)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={r.receipt_type === "REFUND" ? "text-red-700 border-red-200 bg-red-50" : "text-green-700 border-green-200 bg-green-50"}>
                      {r.receipt_type === "REFUND" ? "Devolución" : "Venta"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-num">{fmtEUR(r.net_sales)}</TableCell>
                  <TableCell className="text-right font-num text-slate-500">{fmtEUR((r.total_tax || 0) * (r.sign || 1))}</TableCell>
                  <TableCell className="text-right font-num text-slate-500">{fmtEUR((r.tip || 0) * (r.sign || 1))}</TableCell>
                  <TableCell className="text-right font-num font-semibold">{fmtEUR((r.total_money || 0) * (r.sign || 1))}</TableCell>
                  <TableCell className="text-right font-num text-slate-500">{fmtEUR(r.pay_cash)}</TableCell>
                  <TableCell className="text-right font-num text-slate-500">{fmtEUR(r.pay_card)}</TableCell>
                </TableRow>
              ))}
              {data.receipts.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-10 text-slate-400 text-sm">
                  No hay tickets. Ve a Configuración → Loyverse para conectar e importar el histórico.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <Button data-testid="receipts-prev-page" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
              <span className="text-xs text-slate-500">Página {page} de {pages}</span>
              <Button data-testid="receipts-next-page" variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
